import {
    zodLegalFirstNameValidation,
    zodLegalLastNameValidation,
} from '@fruitful/fruitils';
import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { KeyboardAvoidingView } from 'react-native';
import {
    BodyMediumRegular,
    ControlledInput,
    Form,
    H5Emphasized,
    isNonMobileWeb,
    USE_FORM_MODE_DEFAULTS,
    View,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { preferLegalName } from '../../../../common/utils/bankingUtils';
import { toCardFriendlyName } from '../../../../common/utils/cardUtils';
import { type User } from '../../../../user/types/User';
import type { RunKYCVerifyBody } from '../../../hooks/kyc/useRunKycVerify';
import KycButton from './KycButton';

const contactInfoSchema = z.object({
    firstName: zodLegalFirstNameValidation,
    lastName: zodLegalLastNameValidation,
});

type ContactInfoSchema = z.infer<typeof contactInfoSchema>;

interface EditContactInfoForm {
    onPressNext: (formData?: Partial<RunKYCVerifyBody>) => void;
    user: User;
}

const EditContactInfo = ({ onPressNext, user }: EditContactInfoForm) => {
    const [firstName, lastName] = preferLegalName(user);

    const normalizedFirstName = toCardFriendlyName(firstName);
    const normalizedLastName = toCardFriendlyName(lastName);

    const {
        register,
        control,
        handleSubmit,
        formState: { errors, isValid },
        setFocus,
    } = useForm<ContactInfoSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(contactInfoSchema),
        defaultValues: {
            firstName: normalizedFirstName,
            lastName: normalizedLastName,
        },
    });

    const onSubmit: SubmitHandler<ContactInfoSchema> = ({
        firstName,
        lastName,
    }) => {
        onPressNext({
            firstName,
            lastName,
        });
    };

    return (
        <KeyboardAvoidingView
            style={{ flex: 1 }}
            behavior="padding"
            keyboardVerticalOffset={120}
        >
            <YStack
                flex={1}
                justifyContent="space-between"
                gap={isNonMobileWeb ? 60 : 16}
            >
                <View>
                    <H5Emphasized marginBottom={8}>
                        Edit your verification info
                    </H5Emphasized>
                    <BodyMediumRegular color="$gray1000" marginBottom={24}>
                        Don&apos;t worry, changes here won&apos;t affect your
                        member profile. Your name must match what is on your ID.
                    </BodyMediumRegular>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <YStack gap={12}>
                            <ControlledInput
                                name="firstName"
                                control={control}
                                register={register}
                                errors={errors}
                                autoCapitalize="words"
                                autoComplete="given-name"
                                enterKeyHint="next"
                                onSubmitEditing={() => {
                                    setFocus('lastName');
                                }}
                            />
                            <ControlledInput
                                name="lastName"
                                control={control}
                                register={register}
                                errors={errors}
                                autoCapitalize="words"
                                autoComplete="family-name"
                                enterKeyHint="done"
                                enablesReturnKeyAutomatically={true}
                                onSubmitEditing={handleSubmit(onSubmit)}
                            />
                        </YStack>
                    </Form>
                </View>
                <KycButton
                    text="Save and continue"
                    onPress={handleSubmit(onSubmit)}
                    disabled={!isValid}
                />
            </YStack>
        </KeyboardAvoidingView>
    );
};

export default EditContactInfo;
